import React, { useState } from "react";
import { Upload, Loader2, FileSpreadsheet, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Modal, reviewsApi, inputCls, btnCls, SOURCE_META } from "./shared";

const FIELDS = [
  ["booking_ref", "Booking Ref (eşleştirme için)"], ["reviewer_name", "Müşteri Adı"], ["rating", "Yıldız"],
  ["review_date", "Yorum Tarihi"], ["body", "Yorum Metni"], ["title", "Başlık"], ["place_name", "Ürün / Konum"],
  ["reviewer_country", "Ülke"], ["language", "Dil"], ["external_id", "Yorum ID"], ["owner_reply", "Yanıt"],
];

export default function ImportModal({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [source, setSource] = useState("gyg");
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setPreview(null); setMapping({}); setResult(null); };
  const close = () => { reset(); onClose(); };

  const onFile = async (f) => {
    if (!f) return;
    setFile(f); setResult(null); setBusy(true);
    try {
      const p = await reviewsApi.importPreview(f);
      setPreview(p); setMapping(p.mapping || {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Dosya okunamadı"); setFile(null); }
    setBusy(false);
  };

  const run = async () => {
    if (!mapping.body && !mapping.rating) { toast.error("En az 'Yorum Metni' veya 'Yıldız' sütununu eşleyin"); return; }
    setBusy(true);
    try {
      const r = await reviewsApi.importFile(file, source, mapping);
      setResult(r);
      toast.success(`${r.inserted} yeni yorum eklendi, ${r.matched} tanesi rezervasyonla eşleşti`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "İçe aktarma başarısız"); }
    setBusy(false);
  };

  return (
    <Modal open={open} onClose={close} title="Dosyadan Yorum Aktar (CSV / Excel)" width="max-w-3xl" testId="import-modal">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label className="text-xs text-slate-400">Kaynak
          <select value={source} onChange={e => setSource(e.target.value)} className={`${inputCls} ml-2`} data-testid="import-source">
            {Object.entries(SOURCE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
        </label>
        <label className={`${btnCls} bg-slate-700 hover:bg-slate-600 text-slate-200 cursor-pointer`}>
          <Upload className="w-4 h-4" /> {file ? file.name : "Dosya seç"}
          <input type="file" accept=".csv,.xlsx,.txt" className="hidden" onChange={e => onFile(e.target.files?.[0])} data-testid="import-file-input" />
        </label>
        {busy && <Loader2 className="w-4 h-4 animate-spin text-blue-400" />}
        {preview && <span className="text-xs text-slate-400 ml-auto" data-testid="import-rowcount">{preview.row_count} satır bulundu</span>}
      </div>

      {!preview && !busy && (
        <div className="border border-dashed border-slate-700 rounded-xl p-8 text-center text-sm text-slate-500">
          <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 text-slate-600" />
          GYG / Viator portalından indirdiğiniz yorum dışa aktarımını (CSV veya Excel) seçin. Sütunları bir sonraki adımda eşleyeceksiniz.
        </div>
      )}

      {preview && !result && (
        <>
          <div className="text-xs text-slate-400 mb-2">Sütun eşleme — dosyadaki hangi sütun hangi alana karşılık geliyor? (otomatik tahmin edildi, düzeltebilirsiniz)</div>
          <div className="grid grid-cols-2 gap-2 max-h-[260px] overflow-y-auto pr-1">
            {FIELDS.map(([k, label]) => (
              <label key={k} className="text-xs text-slate-400 flex items-center justify-between gap-2 bg-slate-800/60 border border-slate-700 rounded-lg px-2.5 py-1.5">
                <span className={k === "booking_ref" ? "text-sky-300 font-medium" : ""}>{label}</span>
                <select value={mapping[k] || ""} onChange={e => setMapping(m => ({ ...m, [k]: e.target.value }))} className={`${inputCls} py-1 max-w-[180px]`} data-testid={`import-map-${k}`}>
                  <option value="">— yok —</option>
                  {preview.headers.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </label>
            ))}
          </div>
          {preview.sample?.[0] && (
            <div className="mt-3 text-[11px] text-slate-500 bg-slate-800/40 border border-slate-800 rounded-lg p-2 max-h-[90px] overflow-auto">
              <b className="text-slate-400">Örnek satır:</b> {Object.entries(preview.sample[0]).map(([k, v]) => `${k}=${String(v).slice(0, 40)}`).join(" · ")}
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={close} className={`${btnCls} bg-slate-700 hover:bg-slate-600 text-slate-200`}>Vazgeç</button>
            <button onClick={run} disabled={busy} className={`${btnCls} bg-blue-600 hover:bg-blue-500 text-white`} data-testid="import-run-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {preview.row_count} satırı aktar
            </button>
          </div>
        </>
      )}

      {result && (
        <div className="text-center py-6" data-testid="import-result">
          <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-400 mb-2" />
          <div className="text-white font-semibold">Aktarım tamamlandı</div>
          <div className="text-sm text-slate-300 mt-1">{result.inserted} yeni · {result.updated} zaten vardı · {result.skipped} boş atlandı · <span className="text-emerald-300">{result.matched} rezervasyonla eşleşti</span></div>
          <button onClick={close} className={`${btnCls} bg-slate-700 hover:bg-slate-600 text-slate-200 mx-auto mt-4`}>Kapat</button>
        </div>
      )}
    </Modal>
  );
}
